import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  ChevronUp,
  Copy,
  Download,
  Info,
  RefreshCw,
  Search,
  XCircle
} from "lucide-react";
import API from "../services/api";
import AdminLayout from "./AdminLayout";
import { AdminNotice, useAdminNotice } from "../components/AdminNotice";

function AdminWithdraw() {
  const { notice, showNotice, clearNotice } = useAdminNotice();
  const [requests, setRequests] = useState([]);
  const [utr, setUtr] = useState({});
  const [filter, setFilter] = useState("pending");
  const [query, setQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));

  const loadData = useCallback(async () => {
    try {
      const res = await API.get("/admin/withdraw-requests");
      setRequests(res.data);
    } catch (err) {
      console.log(err);
      showNotice("error", "Load Failed", "Error loading payout data.");
    }
  }, [showNotice]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const approve = async (request) => {
    try {
      await API.post("/admin/approve-withdraw", {
        request_id: request.id,
        utr: utr[request.id] || ""
      });
      await loadData();
      showNotice("success", "Withdrawal Approved", `${formatMoney(request.amount)} approved for ${request.user_name || "user"}.`);
    } catch (err) {
      showNotice("error", "Approve Failed", err?.response?.data?.message || "Unable to approve withdrawal");
    }
  };

  const markPaying = async (id) => {
    try {
      await API.post("/admin/paying-withdraw", { id });
      await loadData();
      showNotice("success", "Marked Paying", "Withdrawal amount is locked while paying.");
    } catch (err) {
      showNotice("error", "Paying Failed", err?.response?.data?.message || "Unable to mark withdrawal as paying");
    }
  };

  const hold = async (id) => {
    try {
      await API.post("/admin/hold-withdraw", { id });
      await loadData();
      showNotice("success", "Withdrawal Held", "Withdrawal request moved to hold.");
    } catch (err) {
      showNotice("error", "Hold Failed", err?.response?.data?.message || "Unable to hold withdrawal");
    }
  };

  const unhold = async (id) => {
    try {
      await API.post("/admin/unhold-withdraw", { id });
      await loadData();
      showNotice("success", "Hold Removed", "Withdrawal request moved back to pending.");
    } catch (err) {
      showNotice("error", "Unhold Failed", err?.response?.data?.message || "Unable to unhold withdrawal");
    }
  };

  const reject = async (id) => {
    try {
      await API.post("/admin/reject-withdraw", { id });
      await loadData();
      showNotice("success", "Withdrawal Rejected", "Withdrawal request rejected.");
    } catch (err) {
      showNotice("error", "Reject Failed", err?.response?.data?.message || "Unable to reject withdrawal");
    }
  };

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return requests.filter((request) => {
      const status = String(request.status || "pending").toLowerCase();
      const matchesFilter = filter === "all" || status === filter;
      const matchesSearch = !search || [
        request.id,
        request.user_name,
        request.user_phone,
        request.agent_name,
        request.amount,
        request.utr,
        request.bank_name,
        request.account_number,
        request.upi_id
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(search));
      const matchesDate = !selectedDate || (request.created_at || "").slice(0, 10) === selectedDate;

      return matchesFilter && matchesSearch && matchesDate;
    });
  }, [filter, query, requests, selectedDate]);

  const summaryRows = useMemo(() => {
    return requests.filter((request) => !selectedDate || (request.created_at || "").slice(0, 10) === selectedDate);
  }, [requests, selectedDate]);

  const pendingCount = countByStatus(summaryRows, "pending");
  const holdCount = countByStatus(summaryRows, "hold");
  const payingCount = countByStatus(summaryRows, "paying");
  const approvedCount = countByStatus(summaryRows, "approved");
  const rejectedCount = countByStatus(summaryRows, "rejected");

  const copyRequestDetails = async (request) => {
    const text = [
      `Request ID: ${request.id}`,
      `Date: ${formatDateTime(request.created_at)}`,
      `User: ${request.user_name || "User"}`,
      `Mobile: ${request.user_phone || "-"}`,
      request.agent_name ? `Agent: ${request.agent_name}` : "",
      `Amount: ${formatMoney(request.amount)}`,
      ...getPaymentLines(request),
      `Status: ${String(request.status || "").toUpperCase()}`,
      request.utr ? `UTR: ${request.utr}` : ""
    ].filter(Boolean).join("\n");

    try {
      await navigator.clipboard.writeText(text);
      showNotice("success", "Copied", "User payout details copied.");
    } catch {
      showNotice("error", "Copy Failed", "Unable to copy details.");
    }
  };

  const exportCsv = () => {
    downloadCsv("payouts.csv", [
      ["Request ID", "Date", "User", "Mobile", "Amount", "Bank", "Account Holder", "Account Number", "IFSC", "UPI", "UTR", "Status"],
      ...filtered.map((request) => [
        request.id,
        formatDateTime(request.created_at),
        request.user_name || "User",
        request.user_phone || "",
        Number(request.amount || 0),
        request.bank_name || "",
        request.account_holder || "",
        request.account_number || "",
        request.ifsc || "",
        request.upi_id || "",
        request.utr || "",
        request.status || ""
      ])
    ]);
  };

  return (
    <AdminLayout>
      <section style={page} className="admin-payout-page">
        <AdminNotice notice={notice} onClose={clearNotice} />
        <style>{payoutResponsiveCss}</style>
        <header style={pageHeader}>
          <div>
            <h1 style={title}>Payout Management</h1>
            <p style={subtitle}>Manage withdrawal requests</p>
          </div>

          <div style={headerActions}>
            <button type="button" style={iconButton} onClick={loadData}><RefreshCw size={14} /></button>
            <button type="button" style={exportButton} onClick={exportCsv}><Download size={14} /> Export</button>
          </div>
        </header>

        <section style={summaryPanel} className="admin-payout-summary">
          <div style={summaryTitle} className="admin-payout-summary-title">
            <span style={summaryIcon}><Info size={15} /></span>
            <span>
              <strong>Payout Summary</strong>
              <small>{selectedDate ? `For ${selectedDate}` : "All dates"}</small>
            </span>
            <ChevronUp size={14} />
          </div>

          <div style={summaryGrid} className="admin-payout-summary-grid">
            <SummaryCard label="Pending" value={pendingCount} foot={formatMoney(sumByStatus(summaryRows, "pending"))} color="#f59e0b" bg="#3f371f" onClick={() => setFilter("pending")} />
            <SummaryCard label="Hold" value={holdCount} foot={formatMoney(sumByStatus(summaryRows, "hold"))} color="#38bdf8" bg="#18354a" onClick={() => setFilter("hold")} />
            <SummaryCard label="Paying" value={payingCount} foot={formatMoney(sumByStatus(summaryRows, "paying"))} color="#a78bfa" bg="#312e81" onClick={() => setFilter("paying")} />
            <SummaryCard label="Approved" value={approvedCount} foot={formatMoney(sumByStatus(summaryRows, "approved"))} color="#10b981" bg="#123f3b" onClick={() => setFilter("approved")} />
            <SummaryCard label="Rejected" value={rejectedCount} foot={formatMoney(sumByStatus(summaryRows, "rejected"))} color="#f87171" bg="#4a2638" onClick={() => setFilter("rejected")} />
            <SummaryCard label="Total Requests" value={summaryRows.length} foot={formatMoney(sumAll(summaryRows))} color="#dbeafe" bg="#334155" onClick={() => setFilter("all")} />
          </div>
        </section>

        <div style={toolbar} className="admin-payout-toolbar">
          <div style={searchBox} className="admin-payout-search">
            <Search size={14} color="#94a3b8" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search user, ID, mobile..."
              style={searchInput}
            />
          </div>

          <label style={datePicker} className="admin-payout-date">
            <CalendarDays size={13} />
            <input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} style={dateInput} />
          </label>
          {selectedDate && (
            <button type="button" style={clearDateButton} className="admin-payout-clear-date" onClick={() => setSelectedDate("")}>All dates</button>
          )}

          {[
            ["pending", "Pending Requests", pendingCount],
            ["hold", "Hold", holdCount],
            ["paying", "Paying", payingCount],
            ["approved", "Approved", approvedCount],
            ["rejected", "Rejected", rejectedCount],
            ["all", "All", requests.length]
          ].map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              className="admin-payout-filter-button"
              style={filter === key ? activeFilter : filterButton}
              onClick={() => setFilter(key)}
            >
              {label} {key === "pending" && <span style={countDot}>{count}</span>}
            </button>
          ))}
        </div>

        <div style={tableWrap} className="admin-payout-table-wrap">
          <table style={table} className="admin-payout-table">
            <thead>
              <tr>
                <th style={th}>Request ID</th>
                <th style={th}>User</th>
                <th style={th}>Amount</th>
                <th style={th}>Payment Details</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((request) => (
                <tr key={request.id} style={tr}>
                  <td style={td} data-label="Request ID">
                    <strong>{request.id}</strong>
                    <small>{formatDateTime(request.created_at)}</small>
                  </td>
                  <td style={td} data-label="User">
                    <div style={userCell}>
                      <span style={avatar}>{getInitial(request.user_name)}</span>
                      <div>
                        <strong style={userName}>{request.user_name || "User"}</strong>
                        <div style={userMeta}>
                          <span>{request.user_phone || "-"}</span>
                          {request.agent_name && <span style={seriesMeta}>Agent: {request.agent_name}</span>}
                          {(request.series || request.series_number) && (
                            <span style={seriesMeta}>Series: {request.series || request.series_number}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={td} data-label="Amount"><strong>{formatMoney(request.amount)}</strong></td>
                  <td style={td} data-label="Payment">
                    <PaymentDetails request={request} />
                  </td>
                  <td style={td} data-label="Status"><StatusBadge status={request.status} /></td>
                  <td style={td} data-label="Actions">
                    <div style={actions} className="admin-payout-actions">
                      {request.status === "pending" ? (
                        <>
                          <input
                            className="admin-payout-utr"
                            style={utrInput}
                            placeholder="Enter UTR"
                            value={utr[request.id] || ""}
                            onChange={(event) => setUtr({ ...utr, [request.id]: event.target.value })}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                approve(request);
                              }
                            }}
                          />
                          <button type="button" style={approveButton} onClick={() => approve(request)}>
                            <CheckCircle2 size={14} /> Approve
                          </button>
                          <button type="button" style={payingButton} onClick={() => markPaying(request.id)}>
                            Paying
                          </button>
                          <button type="button" style={holdButton} onClick={() => hold(request.id)}>
                            Hold
                          </button>
                          <button type="button" style={rejectButton} onClick={() => reject(request.id)}>
                            <XCircle size={14} /> Reject
                          </button>
                        </>
                      ) : request.status === "hold" || request.status === "paying" ? (
                        <>
                          <input
                            className="admin-payout-utr"
                            style={utrInput}
                            placeholder="Enter UTR"
                            value={utr[request.id] || ""}
                            onChange={(event) => setUtr({ ...utr, [request.id]: event.target.value })}
                          />
                          <button type="button" style={approveButton} onClick={() => approve(request)}>
                            <CheckCircle2 size={14} /> Approve
                          </button>
                          {request.status === "hold" ? (
                            <>
                              <button type="button" style={payingButton} onClick={() => markPaying(request.id)}>
                                Paying
                              </button>
                              <button type="button" style={unholdButton} onClick={() => unhold(request.id)}>
                                Unhold
                              </button>
                            </>
                          ) : null}
                          <button type="button" style={rejectButton} onClick={() => reject(request.id)}>
                            <XCircle size={14} /> Reject
                          </button>
                        </>
                      ) : (
                        <div style={actionInfoRow}>
                          <button type="button" style={copyButton} onClick={() => copyRequestDetails(request)}>
                            <Copy size={14} /> Copy
                          </button>
                          <button type="button" style={infoButton}><Info size={14} /></button>
                        </div>
                      )}
                      {(request.status === "pending" || request.status === "hold" || request.status === "paying") && (
                        <button type="button" style={copyButton} onClick={() => copyRequestDetails(request)}>
                          <Copy size={14} /> Copy
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td style={emptyCell} colSpan="6">No payout requests found</td>
                </tr>
              )}
            </tbody>
          </table>

          <footer style={tableFooter}>
            <span>1-{filtered.length} of {filtered.length}</span>
            <span style={pager}>‹ <b>1</b> ›</span>
          </footer>
        </div>
      </section>
    </AdminLayout>
  );
}

function SummaryCard({ label, value, foot, color, bg, onClick }) {
  return (
    <button type="button" style={{ ...summaryCard, background: bg, borderColor: color }} onClick={onClick}>
      <span style={{ color }}>{label}</span>
      <strong>{value}</strong>
      <small style={{ color }}>{foot}</small>
    </button>
  );
}

function PaymentDetails({ request }) {
  const lines = getPaymentLines(request);

  return (
    <div style={paymentBox}>
      {request.utr && (
        <span style={utrBadge}>
          UTR: {request.utr}
          <Copy size={11} />
        </span>
      )}
      {(request.series || request.series_number) && (
        <small style={seriesLine}>Series: {request.series || request.series_number}</small>
      )}
      {lines.length > 0 ? lines.map((line) => <small key={line}>{line}</small>) : <small>No bank details</small>}
      {request.status === "approved" && <small>By: {request.approved_by_name || "Admin"} • {formatDateTime(request.updated_at || request.created_at)}</small>}
      {request.status === "paying" && <small>Amount locked • Paying by: {request.approved_by_name || "Admin"}</small>}
      {request.status === "hold" && request.approved_by_name ? <small>Hold by: {request.approved_by_name}</small> : null}
      {request.status === "rejected" && request.approved_by_name ? <small>Rejected by: {request.approved_by_name}</small> : null}
    </div>
  );
}

function getPaymentLines(request) {
  return request.upi_id
    ? [`UPI: ${request.upi_id}`]
    : [
        request.bank_name && `Bank: ${request.bank_name}`,
        request.account_holder && `Name: ${request.account_holder}`,
        request.account_number && `A/C: ${request.account_number}`,
        request.ifsc && `IFSC: ${request.ifsc}`
      ].filter(Boolean);
}

function StatusBadge({ status }) {
  const styles = {
    pending: pendingBadge,
    hold: holdBadge,
    paying: payingBadge,
    approved: approvedBadge,
    rejected: rejectedBadge
  };
  const labels = {
    pending: "Pending",
    hold: "Hold",
    paying: "Paying",
    approved: "Approved",
    rejected: "Rejected"
  };

  return <span style={styles[status] || pendingBadge}><CheckCircle2 size={12} /> {labels[status] || status}</span>;
}

function sumByStatus(requests, status) {
  return requests
    .filter((request) => String(request.status || "").toLowerCase() === status)
    .reduce((sum, request) => sum + Number(request.amount || 0), 0);
}

function sumAll(requests) {
  return requests.reduce((sum, request) => sum + Number(request.amount || 0), 0);
}

function countByStatus(requests, status) {
  return requests.filter((request) => String(request.status || "").toLowerCase() === status).length;
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function formatDateTime(value) {
  if (!value) return "23 Apr 26, 02:39 pm";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "23 Apr 26, 02:39 pm";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function getInitial(name) {
  return (name || "U").trim().charAt(0).toUpperCase();
}

const page = {
  color: "#111827"
};

const pageHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: "16px"
};

const title = {
  margin: 0,
  fontSize: "18px"
};

const subtitle = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: "12px"
};

const headerActions = {
  display: "flex",
  gap: "6px"
};

const iconButton = {
  width: "30px",
  height: "30px",
  display: "grid",
  placeItems: "center",
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#64748b",
  cursor: "pointer"
};

const exportButton = {
  height: "30px",
  padding: "0 15px",
  display: "flex",
  alignItems: "center",
  gap: "7px",
  border: 0,
  background: "#4f46e5",
  color: "#fff",
  fontWeight: 800,
  fontSize: "12px",
  cursor: "pointer"
};

const summaryPanel = {
  background: "#111827",
  color: "#fff",
  borderRadius: "8px",
  padding: "14px",
  marginBottom: "12px"
};

const summaryTitle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "18px"
};

const summaryIcon = {
  width: "28px",
  height: "28px",
  display: "grid",
  placeItems: "center",
  background: "#1f2937"
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
  gap: "10px"
};

const summaryCard = {
  minHeight: "58px",
  border: "1px solid",
  borderRadius: "8px",
  padding: "10px",
  display: "grid",
  gap: "4px",
  textAlign: "left",
  cursor: "pointer"
};

const toolbar = {
  display: "flex",
  gap: "6px",
  marginBottom: "16px"
};

const searchBox = {
  width: "270px",
  height: "30px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "0 10px",
  background: "#fff",
  border: "1px solid #d1d5db"
};

const searchInput = {
  width: "100%",
  border: 0,
  outline: 0,
  fontSize: "12px"
};

const datePicker = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "0 12px",
  height: "30px",
  border: "1px solid #e5e7eb",
  background: "#fff",
  borderRadius: "10px",
  color: "#334155",
  fontSize: "12px"
};

const dateInput = {
  border: 0,
  outline: 0,
  width: "150px",
  height: "100%",
  fontSize: "12px",
  color: "#0f172a",
  background: "transparent"
};

const clearDateButton = {
  border: 0,
  background: "#f8fafc",
  color: "#475569",
  borderRadius: "10px",
  padding: "0 12px",
  fontSize: "12px",
  cursor: "pointer"
};

const filterButton = {
  height: "30px",
  padding: "0 13px",
  border: 0,
  background: "transparent",
  color: "#334155",
  fontSize: "11px",
  cursor: "pointer"
};

const activeFilter = {
  ...filterButton,
  background: "#fff",
  color: "#2563eb",
  border: "1px solid #e5e7eb"
};

const countDot = {
  marginLeft: "4px",
  background: "#fef3c7",
  color: "#d97706",
  padding: "2px 6px",
  borderRadius: "999px"
};

const tableWrap = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  overflow: "auto"
};

const table = {
  width: "100%",
  minWidth: "1050px",
  borderCollapse: "collapse"
};

const th = {
  height: "38px",
  padding: "0 12px",
  textAlign: "left",
  color: "#64748b",
  fontSize: "9px",
  textTransform: "uppercase",
  borderBottom: "1px solid #e5e7eb"
};

const tr = {
  height: "56px",
  borderBottom: "1px solid #f1f5f9"
};

const td = {
  padding: "8px 12px",
  color: "#111827",
  fontSize: "12px",
  verticalAlign: "middle"
};

const userCell = {
  display: "flex",
  alignItems: "center",
  gap: "10px"
};

const userName = {
  display: "block",
  fontSize: "14px",
  fontWeight: 800,
  color: "#111827",
  textTransform: "uppercase",
  letterSpacing: "0.03em"
};

const userMeta = {
  display: "flex",
  flexDirection: "column",
  gap: "3px",
  marginTop: "3px",
  color: "#475569",
  fontSize: "11px"
};

const seriesMeta = {
  color: "#111827",
  fontWeight: 800,
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};

const avatar = {
  width: "28px",
  height: "28px",
  display: "grid",
  placeItems: "center",
  borderRadius: "50%",
  background: "linear-gradient(135deg, #3b82f6, #7c3aed)",
  color: "#fff",
  fontWeight: 800
};

const paymentBox = {
  display: "grid",
  gap: "3px",
  color: "#64748b"
};

const utrBadge = {
  width: "max-content",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  padding: "3px 8px",
  background: "#d1fae5",
  color: "#047857",
  fontWeight: 800,
  fontSize: "10px"
};

const seriesLine = {
  color: "#111827",
  fontWeight: 800,
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
};

const approvedBadge = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  background: "#d1fae5",
  color: "#047857",
  padding: "5px 10px",
  borderRadius: "999px",
  fontWeight: 800,
  fontSize: "10px"
};

const pendingBadge = {
  ...approvedBadge,
  background: "#fef3c7",
  color: "#b45309"
};

const holdBadge = {
  ...approvedBadge,
  background: "#e0f2fe",
  color: "#0369a1"
};

const payingBadge = {
  ...approvedBadge,
  background: "#ede9fe",
  color: "#6d28d9"
};

const rejectedBadge = {
  ...approvedBadge,
  background: "#fee2e2",
  color: "#dc2626"
};

const actions = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px"
};

const utrInput = {
  width: "120px",
  height: "30px",
  border: "1px solid #d1d5db",
  padding: "0 8px",
  fontSize: "12px"
};

const approveButton = {
  height: "30px",
  display: "flex",
  alignItems: "center",
  gap: "5px",
  border: 0,
  background: "#10b981",
  color: "#fff",
  fontWeight: 800,
  fontSize: "11px",
  cursor: "pointer"
};

const holdButton = {
  ...approveButton,
  background: "#0ea5e9"
};

const payingButton = {
  ...approveButton,
  background: "#7c3aed"
};

const unholdButton = {
  ...approveButton,
  background: "#6366f1"
};

const rejectButton = {
  ...approveButton,
  background: "#ef4444"
};

const infoButton = {
  ...iconButton,
  background: "#f1f5f9"
};

const copyButton = {
  ...approveButton,
  background: "#475569"
};

const actionInfoRow = {
  display: "flex",
  alignItems: "center",
  gap: "6px"
};

const emptyCell = {
  padding: "34px",
  textAlign: "center",
  color: "#94a3b8"
};

const tableFooter = {
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 12px",
  color: "#64748b",
  fontSize: "11px"
};

const pager = {
  display: "flex",
  alignItems: "center",
  gap: "12px"
};

const payoutResponsiveCss = `
  .admin-payout-page {
    min-width: 0;
  }

  .admin-payout-actions {
    flex-wrap: wrap;
    min-width: 0;
  }

  @media (max-width: 1366px) and (min-width: 901px) {
    .admin-payout-summary-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-payout-toolbar {
      flex-wrap: wrap !important;
      align-items: stretch !important;
    }
    .admin-payout-search {
      width: min(100%, 360px) !important;
      flex: 1 1 260px !important;
    }
    .admin-payout-table {
      min-width: 960px !important;
    }
    .admin-payout-actions {
      justify-content: flex-start !important;
    }
  }

  @media (max-width: 900px) {
    .admin-payout-page {
      display: grid;
      gap: 10px;
    }
    .admin-payout-page header {
      display: grid !important;
      grid-template-columns: 1fr auto !important;
      gap: 10px !important;
      align-items: start !important;
      margin-bottom: 0 !important;
    }
    .admin-payout-summary {
      border-radius: 12px !important;
      padding: 9px !important;
      margin-bottom: 0 !important;
    }
    .admin-payout-summary-title {
      display: none !important;
    }
    .admin-payout-summary-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 6px !important;
    }
    .admin-payout-summary-grid button {
      min-height: 54px !important;
      padding: 7px !important;
    }
    .admin-payout-summary-grid button strong {
      font-size: 15px !important;
      line-height: 1 !important;
    }
    .admin-payout-summary-grid button span,
    .admin-payout-summary-grid button small {
      font-size: 10px !important;
      line-height: 1.15 !important;
    }
    .admin-payout-toolbar {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 8px !important;
      margin-bottom: 0 !important;
      align-items: stretch !important;
    }
    .admin-payout-search {
      display: grid !important;
      grid-template-columns: 22px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 8px !important;
      width: 100% !important;
      min-height: 42px !important;
      height: 42px !important;
      border-radius: 12px !important;
    }
    .admin-payout-search input {
      width: 100% !important;
      min-width: 0 !important;
      height: 100% !important;
      min-height: 0 !important;
      padding: 0 !important;
    }
    .admin-payout-date {
      display: grid !important;
      grid-template-columns: 22px minmax(0, 1fr) !important;
      align-items: center !important;
      gap: 8px !important;
      width: 100% !important;
      min-width: 0 !important;
      min-height: 42px !important;
      height: 42px !important;
      border-radius: 12px !important;
    }
    .admin-payout-date input {
      width: 100% !important;
      min-width: 0 !important;
      min-height: 0 !important;
      height: 100% !important;
      padding: 0 !important;
    }
    .admin-payout-toolbar > button {
      width: 100% !important;
      min-height: 40px !important;
      border-radius: 12px !important;
      justify-content: center !important;
      text-align: center !important;
    }
    .admin-payout-filter-button {
      display: none !important;
    }
    .admin-payout-clear-date {
      min-height: 36px !important;
    }
    .admin-payout-table-wrap {
      overflow: visible !important;
      border: 0 !important;
      background: transparent !important;
      border-radius: 0 !important;
    }
    .admin-payout-table {
      display: grid !important;
      min-width: 0 !important;
      width: 100% !important;
      background: transparent !important;
      border-collapse: separate !important;
      border-spacing: 0 !important;
    }
    .admin-payout-table thead {
      display: none !important;
    }
    .admin-payout-table tbody {
      display: grid !important;
      gap: 10px !important;
      width: 100% !important;
    }
    .admin-payout-table tr {
      display: grid !important;
      grid-template-columns: 1fr !important;
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
      padding: 10px !important;
      border: 1px solid #e5e7eb !important;
      border-radius: 14px !important;
      background: #fff !important;
      box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05) !important;
    }
    .admin-payout-table td {
      display: grid !important;
      grid-template-columns: 82px minmax(0, 1fr) !important;
      gap: 8px !important;
      width: 100% !important;
      padding: 7px 0 !important;
      border: 0 !important;
      border-bottom: 1px solid #f1f5f9 !important;
      font-size: 12px !important;
      line-height: 1.3 !important;
      color: #111827 !important;
      align-items: start !important;
    }
    .admin-payout-table td::before {
      content: attr(data-label);
      color: #64748b;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: .03em;
      padding-top: 2px;
    }
    .admin-payout-table td:last-child {
      border-bottom: 0 !important;
      padding-bottom: 0 !important;
    }
    .admin-payout-table td > * {
      min-width: 0 !important;
      max-width: 100% !important;
    }
    .admin-payout-actions {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 8px !important;
      width: 100% !important;
      justify-content: stretch !important;
      align-items: stretch !important;
    }
    .admin-payout-actions .admin-payout-utr {
      grid-column: 1 / -1 !important;
      width: 100% !important;
      height: 42px !important;
      border-radius: 10px !important;
      font-size: 16px !important;
    }
    .admin-payout-actions button {
      width: 100% !important;
      min-width: 0 !important;
      min-height: 40px !important;
      height: auto !important;
      justify-content: center !important;
      border-radius: 10px !important;
      white-space: normal !important;
      line-height: 1.1 !important;
      padding: 8px 8px !important;
    }
    .admin-payout-table footer,
    .admin-payout-table-wrap footer {
      display: none !important;
    }
  }

  @media (max-width: 390px) {
    .admin-payout-summary-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    .admin-payout-table td {
      grid-template-columns: 74px minmax(0, 1fr) !important;
      gap: 6px !important;
    }
    .admin-payout-actions {
      grid-template-columns: 1fr !important;
    }
  }
`;

export default AdminWithdraw;
